import { useMemo } from 'react';

import { MenuObject } from '@rbi-ctg/menu';
import { useAcceleratorBadgesOnMenuExperiment } from 'experiments/use-accelerator-badges-on-menu-experiment';
import { IPricingAndAvailabilityFragment } from 'generated/graphql-gateway';
import { useSystemwidePromotionQuery } from 'generated/sanity-graphql';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';

import { buildEntitiesWithBadgeMap } from './utils';

interface IUseAcceleratorBadgesOnMenuProps {
  menuData: MenuObject[];
  skipExposureLogging: boolean;
  getPricingAndAvailability?: (id: string) => IPricingAndAvailabilityFragment | null | undefined;
}

export type AcceleratorBadgeType = 'pointsMultiplier' | 'bonusPoints';

export interface IUseAcceleratorBadgesOnMenu {
  badges: Set<string>;
  pointsMultiplier: number;
  bonusPoints: number;
  showFullMenuBadge: boolean;
  experimentEnabled: boolean;
}

export const useAcceleratorBadgesOnMenu = ({
  menuData,
  skipExposureLogging = false,
  getPricingAndAvailability,
}: IUseAcceleratorBadgesOnMenuProps): IUseAcceleratorBadgesOnMenu => {
  // Log exposure
  const experimentEnabled = useAcceleratorBadgesOnMenuExperiment(skipExposureLogging);

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser, loading } = useLoyaltyUser();

  const promotionId = loyaltyUser?.bestPromotion?.sanityId ?? '';

  const skip = !loyaltyEnabled || loading || !promotionId;

  const { data } = useSystemwidePromotionQuery({
    variables: {
      id: promotionId,
    },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const sanityCartRequirements = useMemo(() => {
    return (
      data?.SystemwidePromotion?.systemwidePromotion?.find(
        req => req?.__typename === 'CartRequirement'
      ) || {}
    );
  }, [data?.SystemwidePromotion?.systemwidePromotion]);

  const hasPaymentMethodRuleSet = useMemo(
    () =>
      !!data?.SystemwidePromotion?.systemwidePromotion?.find(
        req => req?.__typename === 'PaymentMethod'
      ),
    [data?.SystemwidePromotion?.systemwidePromotion]
  );

  const hasCartRequirement = 'cartRequirement' in sanityCartRequirements;

  const cartRequirement: Set<string> = useMemo(() => {
    if (hasCartRequirement && !hasPaymentMethodRuleSet) {
      return new Set(
        sanityCartRequirements.cartRequirement
          ?.map(cartItemRequirement => {
            // Checks for cart item requirement availability
            const _id = cartItemRequirement?.item?._id ?? '';
            const isCartItemRequirementAvailable = !!getPricingAndAvailability?.(_id)?.isAvailable;
            return isCartItemRequirementAvailable ? _id : '';
          })
          .filter(Boolean) ?? []
      );
    }
    return new Set([]);
  }, [
    getPricingAndAvailability,
    hasCartRequirement,
    hasPaymentMethodRuleSet,
    sanityCartRequirements,
  ]);

  const badges = useMemo(() => {
    return buildEntitiesWithBadgeMap(menuData, cartRequirement);
  }, [cartRequirement, menuData]);

  const pointsMultiplier = data?.SystemwidePromotion?.pointsMultiplier ?? 0;
  const bonusPoints = data?.SystemwidePromotion?.bonusPoints ?? 0;
  const showFullMenuBadge = useMemo(() => {
    return !!(
      promotionId &&
      (pointsMultiplier || bonusPoints) &&
      (!hasCartRequirement || sanityCartRequirements.cartRequirement?.length === 0)
    );
  }, [bonusPoints, hasCartRequirement, pointsMultiplier, promotionId, sanityCartRequirements]);

  return {
    experimentEnabled,
    showFullMenuBadge,
    badges,
    pointsMultiplier,
    bonusPoints,
  };
};
